import { AlertTriangle, Clock3, X } from 'lucide-react';
import toast from 'react-hot-toast';

export const ANALYSIS_CAPACITY_CODE = 'HEAVY_WORK_CAPACITY';
export const ANALYSIS_CAPACITY_TITLE = 'Demo processing is busy';
export const ANALYSIS_CAPACITY_MESSAGE =
  "We're currently processing an unusually high number of demos. Please try again in a few minutes.";

const ANALYSIS_CAPACITY_TOAST_ID = 'analysis-capacity';
const CAPACITY_MESSAGE_PATTERN =
  /analysis capacity is currently busy|demo processing is busy|unusually high number of demos/i;

function containsCapacityMarker(value: unknown, depth = 0): boolean {
  if (depth > 3 || value == null) return false;
  if (typeof value === 'string') {
    return value.includes(ANALYSIS_CAPACITY_CODE) || CAPACITY_MESSAGE_PATTERN.test(value);
  }
  if (typeof value !== 'object') return false;

  const record = value as Record<string, unknown>;
  if (record.code === ANALYSIS_CAPACITY_CODE) return true;
  return ['message', 'error', 'data', 'response'].some((key) =>
    containsCapacityMarker(record[key], depth + 1),
  );
}

export function isAnalysisCapacityError(error: unknown): boolean {
  return containsCapacityMarker(error);
}

/**
 * Shows one deduplicated, site-styled capacity notice.
 * Returns true only for the controlled heavy-work capacity response so callers
 * can keep real parser/file errors on their existing error path.
 */
export function showAnalysisCapacityNotice(error: unknown): boolean {
  if (!isAnalysisCapacityError(error)) return false;

  toast.custom(
    (toastState) => (
      <div
        role="status"
        aria-live="polite"
        style={{
          alignItems: 'flex-start',
          background:
            'linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(17, 24, 39, 0.96))',
          border: '1px solid rgba(245, 158, 11, 0.45)',
          borderRadius: 16,
          boxShadow: '0 22px 55px rgba(0, 0, 0, 0.45), 0 0 28px rgba(245, 158, 11, 0.08)',
          color: '#e2e8f0',
          display: 'flex',
          gap: 12,
          maxWidth: 430,
          opacity: toastState.visible ? 1 : 0,
          padding: '14px 14px 14px 16px',
          transform: toastState.visible ? 'translateY(0)' : 'translateY(-10px)',
          transition: 'opacity 180ms ease, transform 180ms ease',
          width: 'calc(100vw - 32px)',
        }}
      >
        <div
          style={{
            alignItems: 'center',
            background: 'rgba(245, 158, 11, 0.13)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
            borderRadius: 11,
            color: '#fbbf24',
            display: 'flex',
            flex: '0 0 auto',
            height: 38,
            justifyContent: 'center',
            width: 38,
          }}
        >
          <AlertTriangle size={19} aria-hidden="true" />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#f8fafc', fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>
            {ANALYSIS_CAPACITY_TITLE}
          </div>
          <div style={{ color: '#cbd5e1', fontSize: 12, lineHeight: 1.55, marginTop: 3 }}>
            {ANALYSIS_CAPACITY_MESSAGE}
          </div>
          <div
            style={{
              alignItems: 'center',
              color: '#fbbf24',
              display: 'flex',
              fontSize: 11,
              fontWeight: 600,
              gap: 5,
              marginTop: 8,
            }}
          >
            <Clock3 size={13} aria-hidden="true" />
            Your request was not started.
          </div>
        </div>

        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={() => toast.dismiss(toastState.id)}
          style={{
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            color: '#94a3b8',
            cursor: 'pointer',
            display: 'flex',
            flex: '0 0 auto',
            justifyContent: 'center',
            padding: 3,
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    ),
    {
      duration: 12_000,
      id: ANALYSIS_CAPACITY_TOAST_ID,
      position: 'top-right',
    },
  );

  return true;
}
